/**
 * @file Area damage from `Explosive` entities (GDD §Fase 3 — nuevos enemigos).
 *
 * Two ways in. An entity with health detonates when it dies, which is what the
 * exploder enemies do. A projectile has no health to lose, so it opts into
 * `detonateOnDestroy` and goes off however its flight ends — wall, target,
 * range or timeout are all just "the entity is gone" from here.
 *
 * Like every other damage source it only emits `damage` events: the health
 * system remains the sole authority over hit points.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Explosive,
  ExplosiveTrigger,
  Transform,
  Health,
  Collider,
  Projectile,
  Lifetime,
  Destructible
} from '../components/index.js';
import { DetonationCause } from '../components/Explosive.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';

export class ExplosionSystem extends System {
  static priority = 72;

  /**
   * How many generations a chain reaction may propagate. The first barrel is
   * generation 0; a barrel it ignites is generation 1, and so on. Once the
   * chain reaches this depth, further blasts still hurt bodies but stop breaking
   * more props — a hard cap so a barrel field cannot detonate without bound.
   * The per-barrel fuse ({@link Explosive#detonated}) is the other half of the
   * safety: it stops any single barrel going off twice.
   * @type {number}
   */
  static MAX_CHAIN = 5;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {THREE.Vector3} @private */
    this._origin = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._up = new THREE.Vector3(0, 1, 0);
    /** @type {object} @private */
    this._damageEvent = { target: null, amount: 0, source: null, point: this._origin };
    /** @type {object} @private */
    this._appliedEvent = { target: null, amount: 0, distance: 0, self: false };
    /**
     * Blasts queued while another blast is mid-resolution. Damage handlers can
     * kill a second exploder, and that death must not clobber the in-flight
     * origin / event payloads of the first.
     * @type {{ entity: import('../ecs/Entity.js').Entity, cause: string, originX: number, originZ: number, depth: number }[]}
     * @private
     */
    this._pending = [];
    /** @type {boolean} @private */
    this._resolving = false;
    /**
     * Chain generation of the blast currently resolving. A neighbour ignited by
     * it enqueues at `depth + 1`; the counter is what {@link MAX_CHAIN} bounds.
     * @type {number}
     * @private
     */
    this._currentDepth = 0;
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, ({ entity }) =>
        this._detonate(entity, DetonationCause.DEATH)
      ),
      // Destruction covers everything a projectile can do: it is destroyed on
      // impact, on outrunning its range and on timing out.
      this.ctx.events.on(Events.ENTITY_DESTROYED, (entity) => {
        const explosive = entity.get(Explosive);
        if (explosive?.detonateOnDestroy) this._detonate(entity, this._causeOf(entity, explosive));
      })
    );
  }

  /**
   * Works out how a projectile's flight ended. The collision system records an
   * impact directly; anything else is deduced from what the projectile has left.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Entity that is going off.
   * @param {import('../components/Explosive.js').Explosive} explosive Blast data.
   * @returns {string} A {@link DetonationCause}.
   * @private
   */
  _causeOf(entity, explosive) {
    if (explosive.cause) return explosive.cause;

    const projectile = entity.get(Projectile);
    if (projectile && projectile.travelled >= projectile.range) return DetonationCause.RANGE;

    const lifetime = entity.get(Lifetime);
    if (lifetime && lifetime.remaining <= 0) return DetonationCause.TIMEOUT;

    return DetonationCause.RANGE;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Entity that is going off.
   * @param {string} cause Why, for debug and for the expiry event.
   * @returns {void}
   * @private
   */
  _detonate(entity, cause) {
    const explosive = entity.get(Explosive);
    const transform = entity.get(Transform);
    if (!explosive || !transform) return;
    // An exploder that dies is also destroyed a moment later; the latch is what
    // stops that second signal from producing a second blast.
    if (explosive.detonated) return;

    explosive.detonated = true;
    explosive.cause = cause;

    // Snapshot the centre now. Nested blasts (a rocket cooking an exploder mid-
    // resolution) must not rewrite the caller's origin mid-loop.
    const originX = explosive.hasImpact ? explosive.impactX : transform.position.x;
    const originZ = explosive.hasImpact ? explosive.impactZ : transform.position.z;

    if (this._resolving) {
      // A blast set off mid-resolution is one generation deeper than whatever
      // is resolving now; that depth is what caps the chain.
      this._pending.push({ entity, cause, originX, originZ, depth: this._currentDepth + 1 });
      return;
    }

    this._resolving = true;
    try {
      this._currentDepth = 0;
      this._resolve(entity, cause, originX, originZ);
      while (this._pending.length > 0) {
        const next = this._pending.shift();
        this._currentDepth = next.depth;
        this._resolve(next.entity, next.cause, next.originX, next.originZ);
      }
    } finally {
      this._resolving = false;
      this._currentDepth = 0;
    }
  }

  /**
   * Runs one blast to completion against a frozen origin.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Source of the blast.
   * @param {string} cause Why, for debug and for the expiry event.
   * @param {number} originX World X of the blast centre.
   * @param {number} originZ World Z of the blast centre.
   * @returns {void}
   * @private
   */
  _resolve(entity, cause, originX, originZ) {
    const explosive = entity.get(Explosive);
    if (!explosive) return;

    this._origin.set(originX, 0.8, originZ);

    this._announce(explosive, cause);
    this._applyBlast(entity, explosive, originX, originZ);
    this._debug(entity, explosive, cause);
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Source of the blast.
   * @param {import('../components/Explosive.js').Explosive} explosive Blast data.
   * @param {number} originX World X of the blast centre.
   * @param {number} originZ World Z of the blast centre.
   * @returns {void}
   * @private
   */
  _applyBlast(entity, explosive, originX, originZ) {
    const radiusSq = explosive.radius * explosive.radius;

    // Only an environment-affecting blast breaks props, and only while the chain
    // is still within its depth budget. Bodies (players, enemies) are always
    // fair game — the cap and the flag gate destructibles alone.
    const affectsEnvironment = entity.get(ExplosiveTrigger)?.affectsEnvironment === true;
    const canBreakProps = affectsEnvironment && this._currentDepth < ExplosionSystem.MAX_CHAIN;

    for (const target of this.world.query(Health, Transform)) {
      if (target === entity || !target.alive) continue;

      const collider = target.get(Collider);
      if (explosive.mask !== 0 && collider && (explosive.mask & collider.layer) === 0) continue;

      if (target.has(Destructible) && !canBreakProps) continue;

      const position = target.get(Transform).position;
      // Locals, not `this._origin`: a nested blast may rewrite the shared vector
      // while we still have targets left in this loop.
      const dx = position.x - originX;
      const dz = position.z - originZ;
      const distanceSq = dx * dx + dz * dz;
      if (distanceSq > radiusSq) continue;

      const distance = Math.sqrt(distanceSq);
      // Linear falloff from full damage at the centre to zero at the rim.
      const falloff = 1 - distance / explosive.radius;
      // Whoever fired it gets hurt by it, but at a fraction: the shot has to be
      // a mistake worth remembering, not a way to kill yourself outright.
      const isSelf = explosive.ownerId !== 0 && target.id === explosive.ownerId;
      const scale = isSelf ? explosive.selfDamageMultiplier : 1;
      const amount = explosive.damage * falloff * scale;
      if (amount <= 0) continue;

      this._damageEvent.target = target;
      this._damageEvent.amount = amount;
      this._damageEvent.source = entity;
      this._damageEvent.type = 'explosion';
      this.ctx.events.emit(Events.DAMAGE, this._damageEvent);

      this._appliedEvent.target = target;
      this._appliedEvent.amount = amount;
      this._appliedEvent.distance = distance;
      this._appliedEvent.self = isSelf;
      this.ctx.events.emit(Events.EXPLOSION_DAMAGE_APPLIED, this._appliedEvent);
    }
  }

  /**
   * Fires the presentation events. Nothing here knows what listens.
   * @param {import('../components/Explosive.js').Explosive} explosive Blast data.
   * @param {string} cause Why the blast happened.
   * @returns {void}
   * @private
   */
  _announce(explosive, cause) {
    const { events, config } = this.ctx;

    events.emit(Events.EXPLOSION, {
      position: this._origin,
      radius: explosive.radius,
      color: PALETTE.explosion
    });
    events.emit(Events.FX_BURST, {
      position: this._origin,
      direction: this._up,
      count: config.particles.explosionCount,
      options: {
        speed: [8, 26],
        spread: 1.1,
        life: [0.25, 0.7],
        size: [0.2, 0.5],
        color: PALETTE.explosion
      }
    });
    events.emit(Events.FLASH, {
      position: this._origin,
      color: PALETTE.explosion,
      intensity: 22,
      life: 0.32
    });
    // A heavy weapon can ask for more shake than the generic blast; anything
    // that does not care keeps the config value.
    events.emit(Events.SHAKE, {
      amount: explosive.shake > 0 ? explosive.shake : config.feel.trauma.explosion
    });

    if (!explosive.detonateOnDestroy) return;

    events.emit(Events.ROCKET_EXPLODED, {
      position: this._origin,
      radius: explosive.radius,
      damage: explosive.damage,
      ownerId: explosive.ownerId,
      cause
    });
    if (cause === DetonationCause.RANGE || cause === DetonationCause.TIMEOUT) {
      events.emit(Events.ROCKET_EXPIRED, { position: this._origin, cause });
    }
  }

  /**
   * Explains one blast on the console: radius, cause, and what the shooter just
   * did to itself. Off by default — see `config.debug.logExplosions`.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Source of the blast.
   * @param {import('../components/Explosive.js').Explosive} explosive Blast data.
   * @param {string} cause Why the blast happened.
   * @returns {void}
   * @private
   */
  _debug(entity, explosive, cause) {
    if (!this.ctx.config.debug.logExplosions) return;

    const owner = explosive.ownerId ? this.world.getEntity(explosive.ownerId) : null;
    const ownerTransform = owner?.get(Transform);
    let selfDamage = 0;
    if (ownerTransform) {
      const dx = ownerTransform.position.x - this._origin.x;
      const dz = ownerTransform.position.z - this._origin.z;
      const distance = Math.hypot(dx, dz);
      if (distance <= explosive.radius) {
        selfDamage =
          explosive.damage * (1 - distance / explosive.radius) * explosive.selfDamageMultiplier;
      }
    }

    console.debug(
      `[explosion] ${entity.name || 'entity'} cause=${cause} ` +
        `radius=${explosive.radius} centre=${explosive.damage.toFixed(1)} ` +
        `selfDamage=${selfDamage.toFixed(1)}`
    );
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}
